import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { calculateRetirementProjection, calculateAge, normalizeToMonthly } from '../utils/calculations';

const DataContext = createContext();

const PERSONAL_INFO_TABLE = 'member_personal_info_1739560000000';
const RISK_PROFILE_TABLE = 'client_risk_profiles_1739570000000';

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, supabaseConnected } = useAuth();
  
  const [scenarios, setScenarios] = useState([]);
  const [calculations, setCalculations] = useState([]);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [riskProfile, setRiskProfile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setScenarios([]);
        setCalculations([]);
        setPersonalInfo(null);
        setRiskProfile(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      
      if (supabaseConnected) {
        try {
          const [scenariosRes, calculationsRes, infoRes, riskRes, countryRes, currencyRes] = await Promise.all([
            supabase.from('scenarios').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
            supabase.from('calculations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
            supabase.from(PERSONAL_INFO_TABLE).select('*').eq('id', user.id).maybeSingle(),
            supabase.from(RISK_PROFILE_TABLE).select('*').eq('id', user.id).maybeSingle(),
            supabase.from('country').select('*').eq('is_active', true).order('country_name'),
            supabase.from('fx_rates').select('investing_currency').not('investing_currency', 'is', null).order('investing_currency')
          ]);

          if (scenariosRes.error) throw scenariosRes.error;
          if (calculationsRes.error) throw calculationsRes.error;

          setScenarios(scenariosRes.data.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            type: item.type,
            parameters: item.parameters,
            userId: item.user_id,
            createdAt: item.created_at
          })));

          setCalculations(calculationsRes.data.map(item => ({
            id: item.id,
            type: item.type,
            inputs: item.inputs,
            results: item.results,
            userId: item.user_id,
            createdAt: item.created_at
          })));

          if (infoRes.data) setPersonalInfo(infoRes.data);
          if (riskRes.data) setRiskProfile(riskRes.data);
          if (countryRes.data) setCountries(countryRes.data);
          
          if (currencyRes.data) {
            const uniqueCurrencies = [...new Set(currencyRes.data.map(c => c.investing_currency))];
            setCurrencies(uniqueCurrencies);
          }

        } catch (error) {
          console.error('Supabase data fetch failed:', error.message);
        }
      }
      setLoading(false);
    };

    fetchData();
  }, [user, supabaseConnected]);

  const triggerRetirementAutoCalc = async (info, profile) => {
    if (!info || !info.date_of_birth || !info.retirement_age) return;

    try {
      const annualReturn = profile?.["User Target Portfolio Return"]
        ? Number(profile["User Target Portfolio Return"]) * 100
        : 7;

      const retirementInputs = {
        currentAge: calculateAge(info.date_of_birth),
        retirementAge: info.retirement_age,
        currentSavings: info.current_investment_value || 0,
        monthlyContribution: normalizeToMonthly(info.recurring_investment_amount || 0, info.investment_frequency),
        annualReturn: annualReturn,
        inflationRate: 3
      };

      const inputHash = btoa(JSON.stringify(retirementInputs));
      if (info.retirement_calculation_input_hash === inputHash) return;

      const results = calculateRetirementProjection(retirementInputs);
      await saveCalculation({ type: 'retirement', inputs: retirementInputs, results: results });

      const trackingUpdates = {
        id: info.id,
        retirement_calculation_input_hash: inputHash,
        retirement_calculation_last_run_at: new Date().toISOString(),
        retirement_calculation_status: 'completed'
      };

      const { data: updatedInfo, error } = await supabase
        .from(PERSONAL_INFO_TABLE)
        .upsert([trackingUpdates], { onConflict: 'id' })
        .select()
        .single();

      if (error) throw error;
      setPersonalInfo(prev => ({ ...prev, ...updatedInfo }));

    } catch (err) {
      console.error('Auto-calc failed:', err);
    }
  };

  const updatePersonalInfo = async (info) => {
    if (!user) return;
    const updatedInfo = { ...info, id: user.id, updated_at: new Date().toISOString() };
    
    if (supabaseConnected) {
      const { data: savedInfo, error: infoError } = await supabase
        .from(PERSONAL_INFO_TABLE)
        .upsert([updatedInfo], { onConflict: 'id' })
        .select()
        .single();

      if (infoError) throw infoError;
      setPersonalInfo(savedInfo);
      await triggerRetirementAutoCalc(savedInfo, riskProfile);
    }
  };

  const updateRiskProfile = async (profileData) => {
    if (!user) return;
    const data = { ...profileData, id: user.id, updated_at: new Date().toISOString() };
    
    if (supabaseConnected) {
      const { data: savedData, error } = await supabase
        .from(RISK_PROFILE_TABLE)
        .upsert([data], { onConflict: 'id' })
        .select()
        .single();

      if (error) throw error;
      setRiskProfile(savedData);
      await triggerRetirementAutoCalc(personalInfo, savedData);
    }
  };

  const saveCalculation = async (calculation) => {
    if (!user) return;
    const newCalculation = {
      ...calculation,
      id: uuidv4(),
      userId: user.id,
      createdAt: new Date().toISOString()
    };

    if (supabaseConnected) {
      const { error } = await supabase.from('calculations').insert([{
        id: newCalculation.id,
        type: newCalculation.type,
        inputs: newCalculation.inputs,
        results: newCalculation.results,
        user_id: user.id,
        created_at: newCalculation.createdAt
      }]);
      if (error) throw error;
      setCalculations(prev => [newCalculation, ...prev]);
    }
    return newCalculation;
  };

  const saveScenario = async (scenario) => {
    if (!user) return;
    const newScenario = {
      ...scenario,
      id: scenario.id || uuidv4(),
      userId: user.id,
      createdAt: new Date().toISOString()
    };
    if (supabaseConnected) {
      const { error } = await supabase.from('scenarios').insert([{
        id: newScenario.id,
        name: newScenario.name,
        description: newScenario.description,
        type: newScenario.type,
        parameters: newScenario.parameters,
        user_id: user.id,
        created_at: newScenario.createdAt
      }]);
      if (error) throw error;
      setScenarios(prev => [newScenario, ...prev]);
    }
    return newScenario;
  };

  const deleteScenario = async (scenarioId) => {
    if (!user) return;
    if (supabaseConnected) {
      await supabase.from('scenarios').delete().match({ id: scenarioId });
    }
    setScenarios(prev => prev.filter(s => s.id !== scenarioId));
  };

  const value = {
    scenarios,
    calculations,
    personalInfo,
    riskProfile,
    countries,
    currencies,
    saveScenario,
    deleteScenario,
    saveCalculation,
    updatePersonalInfo,
    updateRiskProfile,
    loading
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};